import { createFileRoute } from "@tanstack/react-router"
import { PRODUCT_VISUAL_KINDS } from "@nova-ai/product-visual-templates/kinds"
import { CATALOG_INDEX_STORE, STORE_CODES, type MarketplaceId } from "@nova-ai/shared"
import { Boxes, History, Images, Table2, TrendingUp, WandSparkles } from "lucide-react"
import { useEffect } from "react"
import { z } from "zod"

import { AppShell } from "../components/layout/app-shell"
import { QueryState } from "../components/layout/query-state"
import { ProductOverviewPanel } from "../components/products/product-overview"
import { ProductValuesView } from "../components/products/product-values-view"
import {
  ProductCostHistory,
  type ProductCostFilters,
} from "../components/products/product-cost-history"
import { ProductVisualGallery } from "../components/product-visuals/product-visual-gallery"
import { GalleryStudioView } from "../components/product-visuals/gallery-studio-view"
import { PRODUCT_VISUAL_KIND_ALL } from "../components/product-visuals/product-visual-kind-control"
import { ProductSearchRankingsSections } from "../components/search-rankings/product-search-rankings"
import { SegmentedControl } from "../components/ui/segmented-control"
import { useProductConfigVisuals } from "../services/use-product-config-visuals"
import { useProductOverview } from "../services/use-product-overview"
import { useProductValues } from "../services/use-product-values"

const productDetailSearchSchema = z.object({
  view: z.enum(["values", "visuals", "keywords", "costs"]).optional(),
  costStore: z.string().optional(),
  simpleSku: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  marketplace: z.string().optional(),
  store: z.enum(STORE_CODES).optional().catch(CATALOG_INDEX_STORE),
  kind: z.enum([PRODUCT_VISUAL_KIND_ALL, ...PRODUCT_VISUAL_KINDS]).optional(),
  visualMode: z.enum(["gallery", "studio"]).optional(),
})

function ProductDetailPage() {
  const { sku } = Route.useParams()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const view = search.view ?? "values"
  const store = search.store ?? CATALOG_INDEX_STORE
  const visualKind = search.kind ?? PRODUCT_VISUAL_KIND_ALL
  const visualMode = search.visualMode ?? "gallery"
  const overview = useProductOverview(sku, view !== "costs")
  const values = useProductValues(sku, search.marketplace, view === "values")
  const visuals = useProductConfigVisuals(
    sku,
    store,
    view === "visuals" && visualMode === "gallery"
  )

  // The server validates marketplace membership and chooses the first sorted
  // membership when the URL is absent or invalid. Reflect that answer back into
  // the URL so reload and browser history preserve the actual checklist.
  useEffect(() => {
    const selected = values.data?.selectedMarketplace
    if (view !== "values" || selected === null || selected === undefined) {
      return
    }
    if (search.marketplace === selected) {
      return
    }
    void navigate({
      search: { marketplace: selected },
      replace: true,
    })
  }, [navigate, search.marketplace, values.data?.selectedMarketplace, view])

  const productName = displayProductName(overview.data, sku)
  const selectedMarketplace = values.data?.selectedMarketplace
  const marketplaceOverview = overview.data?.marketplaces.find(
    (entry) => entry.marketplace === selectedMarketplace
  )
  let tabContent
  if (view === "costs") {
    tabContent = (
      <ProductCostHistory
        key={sku}
        sku={sku}
        filters={costFilters(search)}
        onChange={(filters) => {
          void navigate({
            search: {
              view: "costs",
              ...filters,
              store: undefined,
              costStore: filters.store,
            },
          })
        }}
      />
    )
  } else if (view === "visuals") {
    tabContent = (
      <div className="space-y-5">
        <SegmentedControl
          label="Visual workspace"
          value={visualMode}
          options={[
            { value: "gallery", label: "Gallery", icon: Images },
            { value: "studio", label: "Gallery Studio", icon: WandSparkles },
          ]}
          onChange={(nextMode) => {
            void navigate({
              search: {
                view: "visuals",
                store,
                ...(nextMode === "studio" ? { visualMode: "studio" as const } : {}),
              },
            })
          }}
        />

        {visualMode === "studio" ? (
          <GalleryStudioView configSku={sku} store={store} />
        ) : (
          <ProductVisualGallery
            key={`${sku}:${store}`}
            configSku={sku}
            store={store}
            visualKind={visualKind}
            data={visuals.data}
            isLoading={visuals.isLoading}
            error={visuals.error}
            onStoreChange={(nextStore) => {
              void navigate({
                search: {
                  view: "visuals",
                  store: nextStore,
                  ...(visualKind !== PRODUCT_VISUAL_KIND_ALL ? { kind: visualKind } : {}),
                },
              })
            }}
            onVisualKindChange={(kind) => {
              void navigate({
                search: {
                  view: "visuals",
                  ...(search.store !== undefined ? { store: search.store } : {}),
                  ...(kind !== PRODUCT_VISUAL_KIND_ALL ? { kind } : {}),
                },
              })
            }}
          />
        )}
      </div>
    )
  } else if (view === "keywords") {
    tabContent = <ProductSearchRankingsSections sku={sku} />
  } else {
    tabContent = (
      <QueryState isLoading={values.isLoading} error={values.error}>
        {values.data ? (
          <ProductValuesView
            detail={values.data}
            marketplaceOverview={marketplaceOverview}
            onMarketplaceChange={(marketplace: MarketplaceId) => {
              void navigate({ search: { marketplace } })
            }}
          />
        ) : null}
      </QueryState>
    )
  }

  return (
    <AppShell
      title={productName}
      description={`SKU ${sku}`}
      icon={
        <span className="inline-flex size-8 items-center justify-center rounded-lg bg-muted text-muted-foreground">
          <Boxes className="size-4" />
        </span>
      }
      actions={
        <SegmentedControl
          label="Product view"
          size="md"
          className="grid h-auto w-full grid-cols-2 rounded-2xl sm:inline-flex sm:h-9 sm:w-auto sm:rounded-full [&>button]:min-h-11 sm:[&>button]:min-h-0"
          value={view}
          options={[
            { value: "values", label: "Values", icon: Table2 },
            { value: "visuals", label: "Visuals", icon: Images },
            { value: "keywords", label: "Keywords", icon: TrendingUp },
            { value: "costs", label: "Cost history", icon: History },
          ]}
          onChange={(nextView) => {
            void navigate({ search: searchForView(nextView) })
          }}
        />
      }
      fullWidth
    >
      <div className="space-y-6">
        {view !== "costs" ? (
          <ProductOverviewPanel
            overview={overview.data}
            isLoading={overview.isLoading}
            error={overview.error}
          />
        ) : null}

        {tabContent}
      </div>
    </AppShell>
  )
}

function searchForView(view: "values" | "visuals" | "keywords" | "costs") {
  if (view === "values") {
    return {}
  }
  return { view }
}

function costFilters(search: z.infer<typeof productDetailSearchSchema>): ProductCostFilters {
  return {
    ...(search.costStore ? { store: search.costStore } : {}),
    ...(search.simpleSku ? { simpleSku: search.simpleSku } : {}),
    ...(search.from ? { from: search.from } : {}),
    ...(search.to ? { to: search.to } : {}),
  }
}

function displayProductName(
  product: { name: string | null } | undefined,
  configSku: string
): string {
  const trimmed = product?.name?.trim() ?? ""
  if (trimmed.length > 0) {
    return trimmed
  }
  return configSku.trim() || "Unnamed product"
}

export const Route = createFileRoute("/products/$sku")({
  validateSearch: productDetailSearchSchema,
  component: ProductDetailPage,
})
